
import { useState, useLayoutEffect } from 'react'
import { useSetAtom } from 'jotai'
import { Box, Button, Typography, Hidden, Stepper, Step, StepLabel, StepConnector, StepIconProps, stepConnectorClasses, styled } from '@mui/material'
import { Error as ErrorIcon} from '@mui/icons-material'
import { OrderProps, OrderEventsProps, TimelineStepProps, OrderByCategoryProps, RequestTrackingByOrderResponse, RequestTrackingByChildrenResponse } from '../../types/tracking'
import { formatDate } from '../../utils/format'
import { translate } from '../../utils/resources'
import { 
    Step0AsStatus0,
    Step0AsStatus1,
    Step0AsStatus2,
    Step0AsStatus3,
    Step1AsStatus0,
    Step1AsStatus1,
    Step1AsStatus2,
    Step1AsStatus3,
    Step2AsStatus0,
    Step2AsStatus1,
    Step2AsStatus2,
    Step2AsStatus3,
    Step3AsStatus0,
    Step3AsStatus1,
    Step3AsStatus2,
    Step3AsStatus3,
    Step5AsStatus0,
    Step5AsStatus1,
    Step5AsStatus2,
    Step5AsStatus3
} from './icons'

import { childrenOrdersOpenState } from '../../state'

import DEFAULT from '../../config/default'

interface TimelineProps {
    parent: RequestTrackingByOrderResponse,
    children?: RequestTrackingByChildrenResponse
}

// const EPG_SUCCESS = 'Aprobado'
const EPG_ERROR = ['pendiente', 'anulado']

// Ecuador: categoría de evento ad-hoc "Aprobado [OK]" asignada al evento "Pedido Creado" (id evento 1)
const EC_APPROVED_CATEGORY_ID = 9

const timelineBuilderDefault = (order: OrderProps, events: Array<OrderEventsProps>) => {

    let activeStep = 0

    const steps: Array<TimelineStepProps> = [
        {
            id: 0,
            event: translate('timeline.builder.default.0'),
            date: null,
            name: null,
            status: 0,
            packages_success: 0,
            packages_error: 0,
            packages_total: 0,
            display_data: true
        },
        {
            id: 1,
            event: translate('timeline.builder.default.1'),
            date: null,
            name: null,
            status: 0,
            packages_success: 0,
            packages_error: 0,
            packages_total: 0,
            display_data: true
        },
        {
            id: 2,
            event: translate('timeline.builder.default.2'),
            date: null,
            name: null,
            status: 0,
            packages_success: 0,
            packages_error: 0,
            packages_total: 0,
            display_data: true
        },
        {
            id: 3,
            event: translate('timeline.builder.default.3'),
            date: null,
            name: null,
            status: 0,
            packages_success: 0,
            packages_error: 0,
            packages_total: 0,
            date_delivery: null,
            display_data: true
        }
    ]

    const reverseEventsArray = events.slice().reverse()

    const timeline = reverseEventsArray.reduce((acc, value, index) => {

        if (value.evento && value.evento.categoriaEvento.id) {
            
            const eventCategoryId = value.evento.categoriaEvento.id

            if (events.length === index + 1) {                
                if ([1, 2].includes(eventCategoryId)) {
                    activeStep = 0
                } else if ([3, 4].includes(eventCategoryId)) {
                    activeStep = 1
                } else if ([5, 6].includes(eventCategoryId)) {
                    activeStep = 2
                } else if ([7, 8].includes(eventCategoryId)) {
                    activeStep = 3
                }
            }

            // en almacen natura
            // categoria 1 y 2
            if (eventCategoryId === 1) {

                acc[0].date = value.fecha
                acc[0].name = value.nombreVisualizacionConsultoraEvento

                // si en almacén transportista esta en estado mayor a 0 
                // quiere decir que ya hay un evento mayor, por eso le asignamos status 3
                acc[0].status = acc[1].status > 0 ? 3 : 1 

            } else if (eventCategoryId === 2) {

                acc[0].date = value.fecha
                acc[0].name = value.nombreVisualizacionConsultoraEvento
                acc[0].status = acc[1].status > 0 ? 3 : 2

            }

            // en almacén transportista
            // categoria 3 y 4
            else if (eventCategoryId === 3) {

                acc[0].status = 3;

                acc[1].date = value.fecha
                acc[1].name = value.nombreVisualizacionConsultoraEvento

                // si en reparto esta en estado mayor a 0 
                // quiere decir que ya hay un evento mayor, por eso le asignamos status 3
                acc[1].status = acc[2].status > 0 ? 3 : 1 

            } else if (eventCategoryId === 4) {

                acc[0].status = 3;

                acc[1].date = value.fecha
                acc[1].name = value.nombreVisualizacionConsultoraEvento
                acc[1].status = acc[2].status > 0 ? 3 : 2

            }

            // en reparto
            // categoria 5 y 6
            else if (eventCategoryId === 5) {

                acc[1].status = 3;

                acc[2].date = value.fecha
                acc[2].name = value.nombreVisualizacionConsultoraEvento
                
                // si en reparto esta en estado mayor a 0 
                // quiere decir que ya hay un evento mayor, por eso le asignamos status 3
                acc[2].status = acc[3].status > 0 ? 3 : 1

            } else if (eventCategoryId === 6) {

                acc[1].status = 3;

                acc[2].date = value.fecha
                acc[2].name = value.nombreVisualizacionConsultoraEvento
                //acc[2].status = acc[3].status > 0 ? 3 : 2
                acc[2].status = 2

            }

            // entrega
            // categoria 7 y 8
            else if (eventCategoryId === 7) {

                acc[2].status = 3; // Event 2 -> Status: Completed

                acc[3].date = value.fecha
                acc[3].name = value.nombreVisualizacionConsultoraEvento
                acc[3].status = order.idEvento === 2 ? 3 : 1 // Event 0 -> Status: Current

            } else if (eventCategoryId === 8) {

                acc[2].status = 3; // Event 2 -> Status: Completed

                acc[3].date = value.fecha
                acc[3].name = value.nombreVisualizacionConsultoraEvento
                acc[3].status = 2 // Event 0 -> Status: Error

            }
        }

        return acc

    }, steps)

    return { timeline, activeStep }

}

const timelineBuilderMultibulto = (
    order: OrderProps,
    events: Array<OrderEventsProps>,
    childrenOrders: Array<OrderProps>, //children listPedido
    childrenStatus: Array<OrderByCategoryProps> // children listPedidosPorCategoria
) => {

    let activeStep = 0

    const steps: Array<TimelineStepProps> = [
        {
            id: 0,
            event: translate('timeline.builder.default.0'),
            date: null,
            name: null,
            status: 0,
            packages_success: 0,
            packages_error: 0,
            packages_total: 0,
            display_data: true
        },
        {
            id: 1,
            event: translate('timeline.builder.default.1'),
            date: null,
            name: null,
            status: 0,
            packages_success: 0,
            packages_error: 0,
            packages_total: 0,
            display_data: true
        },
        {
            id: 2,
            event: translate('timeline.builder.default.2'),
            date: null,
            name: null,
            status: 0,
            packages_success: 0,
            packages_error: 0,
            packages_total: 0,
            display_data: true
        },
        {
            id: 3,
            event: translate('timeline.builder.default.3'),
            date: null,
            name: null,
            status: 0,
            packages_success: 0,
            packages_error: 0,
            packages_total: 0,
            date_delivery: null,
            display_data: true
        }
    ]

    const packagesByCategory = (collection: Array<OrderByCategoryProps>, categoryId: number): number => {
        let total = 0
        for (const item of collection) {
            if (item.categoria === categoryId) {
                total = item.cantidadCajas || 0
            }
        }
        return total
    }

    const reverseEventsArray = events.slice().reverse()

    const timeline = reverseEventsArray.reduce((acc, value, index) => {

        if (value.evento && value.evento.categoriaEvento.id) {
            
            const eventCategoryId = value.evento.categoriaEvento.id

            if (events.length === index + 1) {
                if ([1, 2].includes(eventCategoryId)) {
                    activeStep = 0
                } else if ([3, 4].includes(eventCategoryId)) {
                    activeStep = 1
                } else if ([5, 6].includes(eventCategoryId)) {
                    activeStep = 2
                } else if ([7, 8].includes(eventCategoryId)) {
                    activeStep = 3
                }
            }

            // en almacen natura
            // categoria 1 y 2
            if (eventCategoryId === 1 || eventCategoryId === 2) {

                const success = packagesByCategory(childrenStatus || [], 1)
                const error = packagesByCategory(childrenStatus || [], 2)
                const total = success + error

                acc[0].date = value.fecha
                acc[0].name = value.nombreVisualizacionConsultoraEvento
                acc[0].status = 1 // Event 0 -> Status: Current

                if (total === 0) {
                    acc[0].status = 3; // Event 0 -> Status: Pasado
                }

                acc[0].packages_success = success
                acc[0].packages_error = error
                acc[0].packages_total = total

                // console.log(value.nombreVisualizacionConsultoraEvento, acc[0])

            }

            // en almacén transportista
            // categoria 3 y 4
            else if (eventCategoryId === 3 || eventCategoryId === 4) {

                const success = packagesByCategory(childrenStatus || [], 3)
                const error = packagesByCategory(childrenStatus || [], 4)
                const total = success + error

                acc[1].date = value.fecha
                acc[1].name = value.nombreVisualizacionConsultoraEvento
                acc[1].display_data = false // transporte

                if (total === 1) {
                    acc[1].display_data = true // transporte
                }

                if (order.idCategoriaEvento === 3 || order.idCategoriaEvento === 4) {
                    if (success === childrenOrders.length) { // todos los paquetes en este estado
                        acc[1].status = 1
                    } else {
                        if (error === childrenOrders.length) {
                            acc[1].status = 2; // todos los paquetes en este estado con error
                        } else {
                            if (total < childrenOrders.length) {
                                acc[1].status = 4; // algunos paquetes
                            }
                        }
                    }
                } else {
                    if (order.idCategoriaEvento > 4) {

                        // status pasado lo seteo por default al inicio
                        acc[1].status = 3

                        // si el pedido ya esta en reparto o parcialmente entregado
                        // fijarse si hay cajas en almacen natura
                        const transporte_status1Success = packagesByCategory(childrenStatus || [], 1)
                        const transporte_status1Error = packagesByCategory(childrenStatus || [], 2)
                        const transporte_status1Total = transporte_status1Success + transporte_status1Error

                        if (transporte_status1Total > 0) {
                            acc[1].status = 4 // algunos paquetes
                        }

                    }
                }

                acc[1].packages_success = success;
                acc[1].packages_error = error;
                acc[1].packages_total = total;

                // console.log(value.nombreVisualizacionConsultoraEvento, acc[1])

            }

            // en reparto
            // categoria 5 y 6
            else if (value.evento.categoriaEvento.id === 5 || value.evento.categoriaEvento.id === 6) {

                const success = packagesByCategory(childrenStatus || [], 5)
                const error = packagesByCategory(childrenStatus || [], 6)
                const total = success + error

                acc[2].date = value.fecha
                acc[2].name = value.nombreVisualizacionConsultoraEvento

                acc[1].display_data = false // transporte
                acc[2].display_data = false // reparto

                // display info
                if (total === 1) {
                    acc[1].display_data = true // transporte
                    acc[2].display_data = true // reparto

                    // reemplazo el name del estado por el unico hijo
                    if (childrenOrders.length > 0 && childrenOrders[0].nombreVisualizacionConsultoraEvento !== null) {
                        acc[2].name = childrenOrders[0].nombreVisualizacionConsultoraEvento;
                    }
                }

                // el pedido principal esta en una categoria de reparto
                if (order.idCategoriaEvento === 5 || order.idCategoriaEvento === 6) {

                    acc[2].status = 1

                    if (success === childrenOrders.length) { // todos los paquetes en este estado
                        // acc[2].status = 1;
                    } else {
                        if (error === childrenOrders.length) {
                            acc[2].status = 2; // todos los paquetes en este estado con error
                        } else {
                            if (total < childrenOrders.length) {
                                acc[2].status = 4 // algunos paquetes
                            }
                        }
                    }

                } else { // el pedido principal esta en entrega

                    // si el pedido ya esta en reparto o parcialmente entregado
                    // fijarse si hay cajas en almacen natura
                    if (order.idCategoriaEvento > 6) {

                        // status pasado lo seteo por default al inicio
                        acc[2].status = 3

                        const reparto_status1Success = packagesByCategory(childrenStatus || [], 1)
                        const reparto_status1Error = packagesByCategory(childrenStatus || [], 2)
                        const reparto_status2Success = packagesByCategory(childrenStatus || [], 3)
                        const reparto_status2Error = packagesByCategory(childrenStatus || [], 4)

                        const reparto_statusTotal = reparto_status1Success + reparto_status1Error + reparto_status2Success + reparto_status2Error;

                        if (reparto_statusTotal > 0) {
                            acc[2].status = 4; // algunos paquetes
                        }
                    }
                    
                }

                acc[2].packages_success = success
                acc[2].packages_error = error
                acc[2].packages_total = total

                // console.log(value.nombreVisualizacionConsultoraEvento, acc[2])

            }

            // entrega
            // categoria 7 y 8
            else if (value.evento.categoriaEvento.id === 7 || value.evento.categoriaEvento.id === 8) {

                acc[1].display_data = false // transporte
                acc[2].display_data = false // reparto

                const success = packagesByCategory(childrenStatus || [], 7)
                const error = packagesByCategory(childrenStatus || [], 8)
                const total = success + error

                acc[3].date = null
                acc[3].name = value.nombreVisualizacionConsultoraEvento
                acc[3].date_delivery = order.fechaEntrega

                // en reparto no se muesta info
                acc[3].display_data = total === 1 ? true : false

                if (success === childrenOrders.length) { // todos los paquetes en este estado
                    acc[3].status = 3
                    acc[3].date = null
                    acc[3].date_delivery = null
                    acc[3].display_data = true
                } else {
                    if (error === childrenOrders.length) {
                        acc[3].status = 2 // todos los paquetes en este estado con error
                    } else {
                        if (total < childrenOrders.length) {
                            acc[3].status = 4 // algunos paquetes 
                        }
                    }
                }

                acc[3].packages_success = success
                acc[3].packages_error = error
                acc[3].packages_total = total

                // console.log(value.nombreVisualizacionConsultoraEvento, acc[3])

            } 

        }

        return acc
    }, steps)

    return { timeline, activeStep }

}

const Timeline = ({ parent, children }: TimelineProps) => {

    const order = parent.pedido
    const epg = parent.epg
    const events = parent.listEstadoPedido

    let childrenOrders: Array<OrderProps> = []
    let childrenStatus: Array<OrderByCategoryProps> = []

    if (children) {
        childrenOrders = children.listPedido
        childrenStatus = children.listPedidosPorCategoria
    }

    const [timeline, setTimeline] = useState<Array<TimelineStepProps>>([])
    const [activeStep, setActiveStep] = useState<number>(0)
    const [activeMobileStep, setActiveMobileStep] = useState<number>(0)

    const [hasChildren, setHasChildren] = useState<boolean>(false)

    const elStatus = (index: number, step: TimelineStepProps) => {
        let array: Array<number | string> = [index+1, step.status]
        if (step.packages_error > 0 && step.packages_error < order.bultosPorPedido) {
            array.push('error_at_least_1')
        } else {
            array.push('success')
        }
        return array.join('-')
    }
    
    useLayoutEffect(() => {
        
        setTimeline([])
        setActiveStep(0)
        setActiveMobileStep(0)  

        if ((DEFAULT.api.countryCode && ['AR', 'MX'].includes(DEFAULT.api.countryCode)) && order.tieneHijos && order.idEvento !== 0) {
            if (childrenOrders.length > 0 && childrenStatus.length > 0) {

                const tml = timelineBuilderMultibulto(order, events, childrenOrders, childrenStatus)

                setHasChildren(true)

                setActiveStep(tml.activeStep)
                setActiveMobileStep(tml.activeStep)  

                if (epg) {
                    let status = 1
                    
                    let name = epg.nombre?.toLowerCase()
                    if (name === 'pendiente') {
                        status = 6
                    } else if (EPG_ERROR.includes(name)) {
                        status = 2
                    }
    
                    let date: string | null = null
                    if (name !== 'aprobado') {
                        date = epg.date
                    }
    
                    tml.timeline.unshift({
                        id: -1,
                        event: epg.nombre,
                        date: date,
                        name: epg.detalle,
                        status: status,
                        packages_success: 0,
                        packages_error: 0,
                        packages_total: 0,
                        display_data: true
                    })
    
                    setActiveStep(tml.activeStep + 1)
                    setActiveMobileStep(tml.activeStep + 1)
                }

                setTimeline(tml.timeline)

            }
        } else { 

            let tml = timelineBuilderDefault(order, events)

            setActiveStep(tml.activeStep)
            setActiveMobileStep(tml.activeStep) 

            if (epg) {
                let status = 1
                
                let name = epg.nombre?.toLowerCase()
                if (name === 'pendiente') {
                    status = 6
                } else if (EPG_ERROR.includes(name)) {
                    status = 2
                }

                let date: string | null = null
                if (name !== 'aprobado') {
                    date = epg.date
                }

                tml.timeline.unshift({
                    id: -1,
                    event: epg.nombre,
                    date: date,
                    name: epg.detalle,
                    status: status,
                    packages_success: 0,
                    packages_error: 0,
                    packages_total: 0,
                    display_data: true
                })

                setActiveStep(tml.activeStep + 1)
                setActiveMobileStep(tml.activeStep + 1)
            }

            if (DEFAULT.api.countryCode === 'EC') {
                const approvedEvent = events.find((value) => value.evento?.categoriaEvento.id === EC_APPROVED_CATEGORY_ID)

                tml.timeline.unshift({
                    id: -1,
                    event: 'Aprobado',
                    date: null,
                    name: null,
                    status: approvedEvent ? 3 : 0,
                    packages_success: 0,
                    packages_error: 0,
                    packages_total: 0,
                    display_data: true
                })

                setActiveStep(tml.activeStep + 1)
                setActiveMobileStep(tml.activeStep + 1)
            }

            setTimeline(tml.timeline)

        }
    
    // eslint-disable-next-line
    }, [])

    return ( 
        <Box sx={(theme) => ({
            my: 5,
            [theme.breakpoints.down('md')]: {
                my: 2
            }
        })}>
            { timeline.length > 0 ? (
                <>
                    <Hidden mdUp>
                        <Typography component="p" variant="caption" color="textSecondary" align="center" sx={{ mb: 2 }}>
                            (Haga clic en cada evento para ver el detalle de cada uno)
                        </Typography>
                    </Hidden>
                    <Stepper alternativeLabel activeStep={activeStep} connector={<TimelineConnectorStyled />}>
                        {timeline.map((step, index) => {
                            return (
                                <Step key={`${index}-${new Date().getTime()}`} data-testid={`step-number-${index}`} onClick={() => setActiveMobileStep(index)}>
                                    <StepLabel icon={elStatus(step.id, step)} StepIconComponent={StepIconEvent}>
                                        <Hidden mdDown>
                                            <StepDataEvent order={order} step={step} index={index} />
                                            {(hasChildren && step.packages_total > 0) && (
                                                <StepDataPackages total={step.packages_total} />
                                            )}
                                        </Hidden>
                                    </StepLabel>
                                </Step>
                            )
                        })} 
                    </Stepper>
                    <Hidden mdUp>
                        <StepperDeviceContainerStyled>
                            <div>
                                <StepDataEvent order={order} step={timeline[activeMobileStep]} index={activeMobileStep} />
                                {(hasChildren && timeline[activeMobileStep].packages_total > 0) && (
                                    <StepDataPackages total={timeline[activeMobileStep].packages_total} />
                                )}
                            </div>
                        </StepperDeviceContainerStyled>
                    </Hidden>
                </>
            ) : (
                <div>loading...</div>
            )}
        </Box>
    )
}

const icons: { [index: string]: Array<React.ReactElement> } = {
    0: [<Step5AsStatus0 />, <Step5AsStatus1 />, <Step5AsStatus2 />, <Step5AsStatus3 />, <Step5AsStatus3 />, <Step5AsStatus3 />, <Step5AsStatus0 />],
    1: [<Step0AsStatus0 />, <Step0AsStatus1 />, <Step0AsStatus2 />, <Step0AsStatus3 />, <Step0AsStatus3 />],
    2: [<Step1AsStatus0 />, <Step1AsStatus1 />, <Step1AsStatus2 />, <Step1AsStatus3 />, <Step1AsStatus3 />],
    3: [<Step2AsStatus0 />, <Step2AsStatus1 />, <Step2AsStatus2 />, <Step2AsStatus3 />, <Step2AsStatus3 />],
    4: [<Step3AsStatus0 />, <Step3AsStatus1 />, <Step3AsStatus2 />, <Step3AsStatus3 />, <Step3AsStatus3 />]
}

const StepIconEvent = ({ icon, active, completed, error, className }: StepIconProps) => {

    // status 0 = inactive
    // status 1 = current
    // status 2 = error
    // status 3 = success
    // status 4 = multi-bulto-algunas-cajas
    // status 5 = multi-bulto-error-todas-las-cajas
    // status 6 = pending --> epg pending

    const statusClasses: Array<string> = [
        'inactive', 'current', 'error', 'success',
        'currentHalf', 'pastHalf',
        'pending'
    ]

    let args: Array<string | number> = (icon && typeof icon === 'string')
        ? icon.split('-')
        : [0, 0]

    let position: number = Number(args[0])
    let status: number = Number(args[1])
    let message: string = String(args[2])

    let style: string = statusClasses[status]

    return (
        <ColorlibStepIconRootStyled ownerState={{ completed, active, error, style }} className={className} data-testid={`step-icon-${position}-${status}-${style}`}>
            {icons[position][status]}
            { message === 'error_at_least_1' && (
                <StepSecondaryIconStyled>
                    <ErrorIcon color="error" fontSize="large"/>
                </StepSecondaryIconStyled>
            )}
        </ColorlibStepIconRootStyled>
    )

}

interface StepDataEventProps {
    order: OrderProps, 
    step: TimelineStepProps,
    index: number
}

const StepDataEvent = ({ order, step, index }: StepDataEventProps) => {
    return (
        <>
            <StepLabelEvent name={step.event} status={step.status} gutterTop={false} />
            {step.display_data && (
                <>
                    {step.date && (
                        <Typography variant="caption">
                            {formatDate(step.date)}
                        </Typography>
                    )}

                    {step.date_delivery && (
                        <div>
                            <span>Fecha estimada de entrega</span>
                            <br />
                            <Typography variant="caption">{formatDate(step.date_delivery, 'dddd DD.MM.YYYY')}</Typography>
                        </div>
                    )}

                    {step.name && (
                        <Typography component="p" variant="subtitle2">{step.name}</Typography>
                    )}
                </>
            )}
            {(index === 3 && step.status === 0) && (
                <>
                    {(!order.fechaEntregaHasta && order.fechaEntrega) && (
                        <>
                            {(order.fechaEntregaFueModificada && order.idEvento !== 2) && (
                                <Typography variant="subtitle2">
                                    Se ha reprogramado la fecha
                                </Typography>
                            )}
                            <Typography component="p" variant="subtitle2">
                                Fecha estimada de entrega
                            </Typography>

                            <Typography variant="caption">
                                {formatDate(order.fechaEntrega, 'dddd DD.MM.YYYY')}
                            </Typography>
                        </>
                    )}
                    {order.fechaEntregaHasta && (
                        <Typography>
                            Llega entre el {formatDate(order.fechaEntrega, 'd')}
                            y el {formatDate(order.fechaEntregaHasta, 'd')}
                            de <span>{formatDate(order.fechaEntregaHasta, 'MMMM')}</span>
                            {(order.fechaEntregaFueModificada && order.idEvento !== 2) && (
                                <span>Se ha reprogramado la fecha</span>
                            )}
                        </Typography>
                    )}
                </>
            )}
        </>
    )
}

interface StepLabelEventProps {
    name: string,
    status: number,
    gutterTop?: boolean,
    gutterBottom?: boolean
}

const StepLabelEvent = ({ name, status, gutterTop = true, gutterBottom = true }: StepLabelEventProps) => {

    const statusClasses: Array<string> = [
        'inactive', 'current', 'error', 'success',
        'currentHalf', 'pastHalf',
        'pending'
    ]

    let style: string = statusClasses[status]

    return (
        <StepLabelEventTextStyled ownerState={{ style, gutterTop, gutterBottom }}>
            {name}
        </StepLabelEventTextStyled>
    )
}

interface StepDataPackagesProps {
    total: number
}

const StepDataPackages = ({ total }: StepDataPackagesProps) => {
    const setOpen = useSetAtom(childrenOrdersOpenState)
    return (
        <StepDataPackagesContainerStyled>
            <Button onClick={() => setOpen(true)}>{ total } { total > 1 ? 'Cajas' : 'Caja' }</Button>
        </StepDataPackagesContainerStyled>
    )
}

const StepperDeviceContainerStyled = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    textAlign: 'center',
    marginTop: theme.spacing(1)
}))

const StepDataPackagesContainerStyled = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2)
}))

const StepLabelEventTextStyled = styled(Typography)<{ownerState: { style: string, gutterBottom?: boolean, gutterTop?: boolean }}>(({ theme, ownerState }) => ({
    fontWeight: theme.typography.fontWeightBold,
    
    textAlign: 'center',
    marginBottom: ownerState.gutterBottom ? theme.spacing(1) : 0,
    marginTop: ownerState.gutterTop ? theme.spacing(1) : 0,
    ...((ownerState.style === 'inactive') && {
        color: theme.palette.grey[600]
    }),
    ...((ownerState.style === 'current') && {
        color: theme.palette.success.main
    }),
    ...((ownerState.style === 'success') && {
        color: theme.palette.success.main
    }),
    ...((ownerState.style === 'error') && {
        color: theme.palette.error.main
    }),
    ...((ownerState.style === 'pending') && {
        color: theme.palette.error.main
    }),
}))

const TimelineConnectorStyled = styled(StepConnector)(({ theme }) => ({
    [theme.breakpoints.up('sm')]: {
        left: 'calc(-50% + 50px)',
        right: 'calc(50% + 50px)',
        [`&.${stepConnectorClasses.alternativeLabel}`]: {
            top: 44
        }
    },
    [theme.breakpoints.down('sm')]: {
        [`&.${stepConnectorClasses.alternativeLabel}`]: {
            top: 24
        }
    },
    [`&.${stepConnectorClasses.active}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            background: theme.palette.success.main
        }
    },
    [`&.${stepConnectorClasses.completed}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            background: theme.palette.success.main
        }
    },
    [`& .${stepConnectorClasses.line}`]: {
        height: 6,
        border: 0,
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
        borderRadius: 3
    }
}))

const ColorlibStepIconRootStyled = styled('div')<{ ownerState: { 
    style: string, completed?: boolean; active?: boolean; error?: boolean 
}}>(({ theme, ownerState }) => ({
    backgroundColor: theme.palette.grey[200],
    zIndex: 1,
    color: '#fff',
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    [theme.breakpoints.up('sm')]: {
        width: 92,
        height: 92,
    },
    [theme.breakpoints.down('sm')]: {
        width: 52,
        height: 52
    },
    ...((ownerState.style === 'current') && {
        background: theme.palette.success.main
    }),

    ...((ownerState.style === 'success') && {
        background: theme.palette.success.main
    }),

    ...((ownerState.style === 'error') && {
        background: theme.palette.error.main
    }),

    ...((ownerState.style === 'currentHalf') && {
        background: `linear-gradient(to right, ${theme.palette.success.main} 0%, ${theme.palette.success.main} 50%, ${theme.palette.grey[200]} 50%, ${theme.palette.grey[200]} 100%)`
    }),

    ...((ownerState.style === 'pastHalf') && {
        background: `linear-gradient(to right, ${theme.palette.success.main} 0%, ${theme.palette.success.main} 50%, ${theme.palette.grey[200]} 50%, ${theme.palette.grey[200]} 100%)`
    }),

    ...((ownerState.style === 'pending') && {
        background: theme.palette.error.main
    }),
}))

const StepSecondaryIconStyled = styled(Box)({
    position: 'absolute',
    top: -8,
    right: -8,
    lineHeight: 1
})

export default Timeline
export { StepIconEvent, StepLabelEvent }
